import { useRef } from 'react'
import type { NormRect } from '../api'

interface Props {
  rect: NormRect
  /** CSS pixel size of the page the box sits on. */
  pageWidth: number
  pageHeight: number
  onChange: (next: NormRect) => void
  label?: string
  readOnly?: boolean
  /** Colour for this box, so each recipient's field is identifiable at a glance. */
  tone?: string
  /** Muted boxes belong to a recipient other than the one being edited. */
  muted?: boolean
  /** Called when a muted box is clicked, to switch editing to its owner. */
  onSelect?: () => void
  /**
   * Pulls a dragged box onto a blank line it is nearly on. Holding Alt places
   * it exactly where the pointer is, for the document whose blank is not where
   * the handwriting goes.
   */
  snap?: (next: NormRect) => NormRect
}

const MIN_W = 0.06
const MIN_H = 0.02

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

/**
 * FieldBox is the draggable, resizable signature placeholder.
 *
 * Its position is held as fractions of the page rather than pixels, so the
 * placement is identical whatever the zoom level, window size or screen
 * density — and it is those fractions that get persisted and later transformed
 * into PDF user space on the server.
 */
export function FieldBox({
  rect, pageWidth, pageHeight, onChange,
  label = 'Signature', readOnly, tone, muted, onSelect, snap,
}: Props) {
  const drag = useRef<{
    mode: 'move' | 'resize'
    startX: number
    startY: number
    origin: NormRect
  } | null>(null)

  function begin(mode: 'move' | 'resize', e: React.PointerEvent) {
    // A muted box is somebody else's. Clicking it selects that recipient
    // rather than dragging a field the sender is not currently editing.
    if (muted) {
      e.preventDefault()
      e.stopPropagation()
      onSelect?.()
      return
    }
    if (readOnly) return
    e.preventDefault()
    e.stopPropagation()
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
    drag.current = { mode, startX: e.clientX, startY: e.clientY, origin: { ...rect } }
  }

  function move(e: React.PointerEvent) {
    const d = drag.current
    if (!d || pageWidth <= 0 || pageHeight <= 0) return
    const dx = (e.clientX - d.startX) / pageWidth
    const dy = (e.clientY - d.startY) / pageHeight

    if (d.mode === 'move') {
      const next = {
        ...d.origin,
        x: clamp(d.origin.x + dx, 0, 1 - d.origin.w),
        y: clamp(d.origin.y + dy, 0, 1 - d.origin.h),
      }
      onChange(snap && !e.altKey ? snap(next) : next)
    } else {
      onChange({
        ...d.origin,
        w: clamp(d.origin.w + dx, MIN_W, 1 - d.origin.x),
        h: clamp(d.origin.h + dy, MIN_H, 1 - d.origin.y),
      })
    }
  }

  function end() {
    drag.current = null
  }

  // Keyboard nudging keeps placement usable without a pointer.
  function key(e: React.KeyboardEvent) {
    if (readOnly || muted) return
    const step = e.shiftKey ? 0.05 : 0.005
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }
    const delta = moves[e.key]
    if (!delta) return
    e.preventDefault()
    onChange({
      ...rect,
      x: clamp(rect.x + delta[0], 0, 1 - rect.w),
      y: clamp(rect.y + delta[1], 0, 1 - rect.h),
    })
  }

  return (
    <div
      className={`field-box${readOnly ? ' is-readonly' : ''}${muted ? ' is-muted' : ''}`}
      style={{
        left: `${rect.x * 100}%`,
        top: `${rect.y * 100}%`,
        width: `${rect.w * 100}%`,
        height: `${rect.h * 100}%`,
        ...(tone ? ({ '--field-tone': tone } as React.CSSProperties) : {}),
      }}
      onPointerDown={(e) => begin('move', e)}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={key}
      tabIndex={readOnly || muted ? -1 : 0}
      role={readOnly || muted ? undefined : 'application'}
      aria-label={readOnly || muted ? undefined : `${label} field. Use arrow keys to move.`}
    >
      <span className="field-box-label">{label}</span>
      {!readOnly && !muted && (
        <span
          className="field-box-handle"
          onPointerDown={(e) => begin('resize', e)}
          onPointerMove={move}
          onPointerUp={end}
          aria-hidden
        />
      )}
    </div>
  )
}
